import { FC, useReducer } from 'react';
import { PipePuzzleTable, PPR, PPD, TransferStation, LocalStorage, Outflow, PipeImg } from './pipe-puzzle.styles';
import { Pipe, PipePuzzle as PP } from 'entity/pipes';
import * as PipeImages from '../../../../public/images/pipes/*.svg';
import { keyDownHook } from 'hooks';

interface PipePuzzleProps { }

const puzzle = generatePuzzle();

function generatePuzzle(): PP {
   while (true) {
      const p = generateRandomPuzzle();
      if (p.solveFor('station') && p.solveFor('storage')) {
         return p;
      }
   }
}

function generateTestPuzzle() {
   const [width, height] = [5, 5];
   // place ports (1-5, station and storage must be different)
   const outflow = rand(1, height);
   const storage = rand(1, height);
   const station = randExcept(1, height, storage);
   const p = new PP(outflow, storage, station, new Array(height));
   // fill puzzle with pipes
   for (let r = 0; r < p.pipes.length; r++) {
      p.pipes[r] = [];
      for (let c = 0; c < width; c++) {
         if (c == 0) {
            p.pipes[r]!.push(Pipe.from([ Pipe.br,  Pipe.lt,  Pipe.horz, Pipe.lb, Pipe.tr ][r] as Pipe));
         } else if (c == 1) {
            p.pipes[r]!.push(Pipe.from([ Pipe.br, Pipe.lt,  Pipe.lb,  Pipe.vert, Pipe.tr ][r] as Pipe));
         } else if (c == 2) {
            p.pipes[r]!.push(Pipe.from([ Pipe.horz, Pipe.br, Pipe.lt,  Pipe.horz, Pipe.horz ][r] as Pipe));
         } else if (c == 3) {
            p.pipes[r]!.push(Pipe.from(Pipe.horz));
         } else {
            p.pipes[r]!.push(Pipe.from([ Pipe.br, Pipe.lt, Pipe.horz, Pipe.lb, Pipe.tr ][r] as Pipe));
         }
      }
   }
   p.lock([2,3]);
   p.solveFor('storage');
   return p;
}

function generateRandomPuzzle(): PP {
   const [width, height] = [5, 5];
   // place ports (1-5, station and storage must be different)
   const outflow = rand(1, height);
   const storage = rand(1, height);
   const station = randExcept(1, height, storage);
   const p = new PP(outflow, storage, station, new Array(height));

   for (let r = 0; r < p.pipes.length; r++) {
      p.pipes[r] = [];
      for (let c = 0; c < width; c++) {
         const randomPipe = Pipe.options[rand(0, Pipe.options.length - 1)] as Pipe;
         p.pipes[r]!.push(Pipe.from(randomPipe));
      }
   }

   const lock1 = rand(0, width - 1);
   p.lock([lock1, randExcept(0, width - 1, lock1)]);
   return p;
}

function rand(min: number, max: number) { // min and max included 
   return Math.floor(Math.random() * (max - min + 1) + min);
}

function randExcept(min: number, max: number, except: number) {
   return (rand(min, max - 2) + except)%5 + min;
}

function PipeImgUrl(id: string) {
   return new URL((PipeImages as any)[id], import.meta.url);
}

// 9 wide, 7 tall
const PipePuzzle: FC<PipePuzzleProps> = function() {
   const [, forceUpdate] = useReducer(x => x + 1, 0);

   keyDownHook((key) => {
      switch(key) {
         case 'arrowup':
         case 'w':
            puzzle.rotateSelectedColumn('up');
            puzzle.processSolution();
            break;
         case 'arrowdown':
         case 's':
            puzzle.rotateSelectedColumn('down');
            puzzle.processSolution();
            break;
         case 'arrowleft':
         case 'a':
            puzzle.shiftSelectedColumn('left');
            break;
         case 'arrowright':
         case 'd':
            puzzle.shiftSelectedColumn('right');
            break;
         default:
            console.error(`Unrecognized key: ${key}`);
      }
      forceUpdate();
   }, []);

   return (<>
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
         <button onClick={() => puzzle.solveFor('storage') && forceUpdate()}>Reset</button>
         <button onClick={() => puzzle.solveFor('station') && forceUpdate()}>Solve</button>
      </div>
      <PipePuzzleTable>
         <thead>
            <PPR>
               <PPD></PPD>
               <PPD></PPD>
               {[...Array(puzzle.numColumns()).keys()].map(c => 
                  puzzle.locks.includes(c) 
                     ? <PPD><PipeImg src={PipeImgUrl('Lock').href} alt={'L'}/></PPD>
                     : puzzle.selected == c
                        ? <PPD><PipeImg src={PipeImgUrl('Up').href} alt={'U'}/></PPD>
                        : <PPD></PPD>
               )}
               <PPD></PPD>
               <PPD></PPD>
            </PPR>
         </thead>
         <tbody>
            {puzzle.pipes.map((row, r) => (
               <PPR>
                  {(puzzle.outflow == r+1) 
                     ? <><Outflow><span>E-710 Outflow &ensp;</span> <PipeImg src={PipeImgUrl('Outflow').href} alt={'#'}/></Outflow><PPD><PipeImg flowing={puzzle.endPipes.outflow.flowing} src={PipeImgUrl(puzzle.endPipes.outflow.ltbr).href} alt={puzzle.endPipes.outflow.tag}/></PPD></>
                     : <><PPD></PPD><PPD></PPD></>}
                  {row.map((pipe, c) => (
                     <PPD><PipeImg flowing={pipe.flowing} src={PipeImgUrl(pipe.ltbr).href} alt={pipe.tag}/></PPD>
                  ))}
                  {(puzzle.storage == r+1) 
                     ? <><PPD><PipeImg flowing={puzzle.endPipes.storage.flowing} src={PipeImgUrl(puzzle.endPipes.storage.ltbr).href} alt={puzzle.endPipes.storage.tag}/></PPD><LocalStorage><PipeImg src={PipeImgUrl('LocalStorage').href} alt={'#'}/><span>&ensp; Local Storage</span></LocalStorage></>
                     : (puzzle.station == r+1)
                        ? <><PPD><PipeImg flowing={puzzle.endPipes.station.flowing} src={PipeImgUrl(puzzle.endPipes.station.ltbr).href} alt={puzzle.endPipes.station.tag}/></PPD><TransferStation><PipeImg src={PipeImgUrl('TransferStation').href} alt={'#'}/> <span>&ensp; Transfer Station</span></TransferStation></>
                        : <><PPD></PPD><PPD></PPD></>}
               </PPR>
            ))}
         </tbody>
         <tfoot>
            <PPR>
               <PPD></PPD>
               <PPD></PPD>
               {[...Array(puzzle.numColumns()).keys()].map(c => 
                  puzzle.locks.includes(c) 
                     ? <PPD><PipeImg src={PipeImgUrl('Lock').href} alt={'L'}/></PPD>
                     : puzzle.selected == c
                        ? <PPD><PipeImg src={PipeImgUrl('Down').href} alt={'D'}/></PPD>
                        : <PPD></PPD>
               )}
               <PPD></PPD>
               <PPD></PPD>
            </PPR>
         </tfoot>
      </PipePuzzleTable>
   </>);
}

export default PipePuzzle;

